/**
 * Smart Breakout Targets — squeeze de volatilidad dual (BB Width + ATR) + ruptura confirmada
 *
 * A diferencia de Squeeze Breakout (un solo engine: rango reciente vs ATR), este
 * indicador exige que dos motores independientes coincidan simultáneamente:
 * BB Width comprimido Y ATR comprimido. Usa un Donchian adaptativo para el rango
 * de compresión, zonas de resistencia/soporte, una regla de "consumo" (la primera
 * vela que cierra más allá del límite decide el destino de la caja) y previene
 * solapamiento de cajas por precio (no por índice de barra).
 */

import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcATR(candles, period) {
  const n = candles.length
  const tr = candles.map((c, i) =>
    i === 0
      ? c.high - c.low
      : Math.max(c.high - c.low, Math.abs(c.high - candles[i - 1].close), Math.abs(c.low - candles[i - 1].close))
  )
  const out = new Array(n).fill(null)
  if (n < period) return out
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  out[period - 1] = sum / period
  for (let i = period; i < n; i++) out[i] = (out[i - 1] * (period - 1) + tr[i]) / period
  return out
}

function calcSMA(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let s = 0, cnt = 0
    for (let j = i - len + 1; j <= i; j++) {
      if (arr[j] != null && !isNaN(arr[j])) { s += arr[j]; cnt++ }
    }
    if (cnt > 0) out[i] = s / cnt
  }
  return out
}

function calcStdevPop(arr, len) {
  const out = new Array(arr.length).fill(null)
  for (let i = len - 1; i < arr.length; i++) {
    let sum = 0, cnt = 0
    for (let j = i - len + 1; j <= i; j++) { sum += arr[j]; cnt++ }
    const mean = sum / cnt
    let sq = 0
    for (let j = i - len + 1; j <= i; j++) sq += (arr[j] - mean) ** 2
    out[i] = Math.sqrt(sq / cnt)
  }
  return out
}

function boxesOverlap(topA, bottomA, topB, bottomB) {
  return !(topA < bottomB || bottomA > topB)
}

// ── detect ─────────────────────────────────────────────────────────────────────

function detect(candles, config) {
  const n = candles.length
  const {
    squeezeLength    = 20,
    bbMult           = 2.0,
    squeezeThresh    = 0.6,
    atrCompressRatio = 0.75,
    minSqueezeBars   = 5,
    impulseMult      = 0.8,
    preventOverlap   = true,
    slBuffer         = 0.5,
    tp1RR            = 1.0,
    tp2RR            = 2.0,
    tp3RR            = 3.0,
    useVolFilter     = false,
    volMult          = 1.5,
  } = config

  const WARMUP = Math.max(squeezeLength, 50)
  if (n < WARMUP + 5) return null

  const closes    = candles.map(c => c.close)
  const bbBasis   = calcSMA(closes, squeezeLength)
  const bbStdev   = calcStdevPop(closes, squeezeLength)
  const bbWidth   = closes.map((_, i) => (
    bbBasis[i] != null && bbStdev[i] != null && bbBasis[i] !== 0
      ? ((bbBasis[i] + bbMult * bbStdev[i]) - (bbBasis[i] - bbMult * bbStdev[i])) / bbBasis[i]
      : null
  ))
  const bbWidthSma = calcSMA(bbWidth, squeezeLength)

  const atrPeriod = Math.max(Math.floor(squeezeLength / 2), 7)
  const atrs      = calcATR(candles, atrPeriod)
  const atrSmas   = calcSMA(atrs, squeezeLength)

  const volumes = candles.map(c => c.volume ?? 0)
  const hasVol  = volumes.some(v => v > 0)
  const volSmas = calcSMA(volumes, 20)

  // ── State machine ────────────────────────────────────────────────────────────
  let sqBars = 0, sqHigh = null, sqLow = null, sqStartIdx = null
  let wasSq = false

  let dir = 0, entry = null, sl = null, tp1 = null, tp2 = null, tp3 = null
  let tradeActive = false, entryIdx = null

  const pendingBoxes = []
  const allBoxes     = []
  const signals      = []
  const sqMarkers    = []

  let lastSqActive = false, lastSqBars = 0
  let winCount = 0, lossCount = 0, earlyCount = 0

  for (let i = WARMUP; i < n; i++) {
    const c    = candles[i]
    const atr  = atrs[i]
    const aSma = atrSmas[i]
    const bbw  = bbWidth[i]
    const bbwSma = bbWidthSma[i]

    if (![atr, aSma, bbw, bbwSma].every(Number.isFinite)) {
      wasSq = false; sqBars = 0; sqHigh = null; sqLow = null; sqStartIdx = null
      continue
    }

    const bbSqueeze   = bbw < bbwSma * squeezeThresh
    const atrCompress = aSma > 0 && atr < aSma * atrCompressRatio
    const isSq = bbSqueeze && atrCompress

    if (isSq) {
      if (!wasSq) {
        sqBars = 1; sqHigh = c.high; sqLow = c.low; sqStartIdx = i
      } else {
        sqBars++
        sqHigh = Math.max(sqHigh, c.high)
        sqLow  = Math.min(sqLow,  c.low)
      }
    } else {
      if (wasSq && sqBars >= minSqueezeBars && sqHigh != null && sqLow != null && sqHigh > sqLow) {
        const rangeSpan = sqHigh - sqLow
        const maxSpan   = atr * 6.0
        let fHigh, fLow
        if (rangeSpan > maxSpan && atr > 0) {
          const center = (sqHigh + sqLow) / 2
          fHigh = center + atr * 3.0
          fLow  = center - atr * 3.0
        } else {
          fHigh = sqHigh
          fLow  = sqLow
        }

        const overlaps = preventOverlap && pendingBoxes.some(b => boxesOverlap(fHigh, fLow, b.top, b.bottom))

        if (!overlaps) {
          const halfAtr = atr * 0.5
          const box = {
            startIdx: sqStartIdx, endIdx: i,
            startTime: candles[sqStartIdx].time, endTime: c.time,
            top: fHigh, bottom: fLow, atr, sqBarCount: sqBars,
            resistanceZoneBottom: fHigh - halfAtr,
            supportZoneTop:       fLow + halfAtr,
          }
          pendingBoxes.push(box)
          allBoxes.push(box)
          sqMarkers.push({ time: c.time })
        }
      }
      sqBars = 0; sqHigh = null; sqLow = null; sqStartIdx = null
    }
    wasSq = isSq
    lastSqActive = isSq
    lastSqBars   = isSq ? sqBars : 0

    const volSma   = volSmas[i]
    const volSurge = hasVol && volSma != null && volSma > 0 && volumes[i] > volSma * volMult
    const volOk    = !useVolFilter || !hasVol || volSurge

    const body  = Math.abs(c.close - c.open)
    const impUp = c.close > c.open && body > atr * impulseMult
    const impDn = c.close < c.open && body > atr * impulseMult

    // ── Monitoreo de trade activo (SL primero, ambigüedad -> LOSS) ──────────
    if (tradeActive && entryIdx !== i && signals.length) {
      const sig = signals[signals.length - 1]
      if (dir === 1) {
        if (sl != null && c.low <= sl) {
          sig.result = 'loss'; sig.pnl = sl - entry; sig.closeTime = c.time
          tradeActive = false; dir = 0; lossCount++
        } else if (tp3 != null && c.high >= tp3) {
          sig.result = 'win'; sig.pnl = tp3 - entry; sig.closeTime = c.time
          tradeActive = false; dir = 0; winCount++
        } else {
          if (tp1 != null && !sig.tp1Hit && c.high >= tp1) sig.tp1Hit = c.time
          if (tp2 != null && !sig.tp2Hit && c.high >= tp2) sig.tp2Hit = c.time
        }
      } else if (dir === -1) {
        if (sl != null && c.high >= sl) {
          sig.result = 'loss'; sig.pnl = entry - sl; sig.closeTime = c.time
          tradeActive = false; dir = 0; lossCount++
        } else if (tp3 != null && c.low <= tp3) {
          sig.result = 'win'; sig.pnl = entry - tp3; sig.closeTime = c.time
          tradeActive = false; dir = 0; winCount++
        } else {
          if (tp1 != null && !sig.tp1Hit && c.low <= tp1) sig.tp1Hit = c.time
          if (tp2 != null && !sig.tp2Hit && c.low <= tp2) sig.tp2Hit = c.time
        }
      }
    }

    // ── Escaneo de breakout con regla de consumo ────────────────────────────
    for (let bi = pendingBoxes.length - 1; bi >= 0; bi--) {
      const box = pendingBoxes[bi]
      const brokeUp   = c.close > box.top
      const brokeDown = c.close < box.bottom
      if (!brokeUp && !brokeDown) continue

      if (brokeUp) {
        if (impUp && volOk) {
          const ent  = c.close
          const s    = box.bottom - atr * slBuffer
          const risk = Math.abs(ent - s)
          const t1 = ent + risk * tp1RR, t2 = ent + risk * tp2RR, t3 = ent + risk * tp3RR
          const strength = (body > atr * impulseMult * 1.5 ? 1 : 0)
            + (volSurge ? 1 : 0)
            + (box.sqBarCount >= minSqueezeBars * 2 ? 1 : 0)
          if (tradeActive) { earlyClose(signals, c.close, c.time); earlyCount++ }
          signals.push({ time: c.time, idx: i, dir: 1, entry: ent, sl: s, tp1: t1, tp2: t2, tp3: t3, strength, result: 'active', pnl: null })
          entry = ent; sl = s; tp1 = t1; tp2 = t2; tp3 = t3
          dir = 1; tradeActive = true; entryIdx = i
        }
        pendingBoxes.splice(bi, 1)
      } else if (brokeDown) {
        if (impDn && volOk) {
          const ent  = c.close
          const s    = box.top + atr * slBuffer
          const risk = Math.abs(s - ent)
          const t1 = ent - risk * tp1RR, t2 = ent - risk * tp2RR, t3 = ent - risk * tp3RR
          const strength = (body > atr * impulseMult * 1.5 ? 1 : 0)
            + (volSurge ? 1 : 0)
            + (box.sqBarCount >= minSqueezeBars * 2 ? 1 : 0)
          if (tradeActive) { earlyClose(signals, c.close, c.time); earlyCount++ }
          signals.push({ time: c.time, idx: i, dir: -1, entry: ent, sl: s, tp1: t1, tp2: t2, tp3: t3, strength, result: 'active', pnl: null })
          entry = ent; sl = s; tp1 = t1; tp2 = t2; tp3 = t3
          dir = -1; tradeActive = true; entryIdx = i
        }
        pendingBoxes.splice(bi, 1)
      }
    }
  }

  // Flush squeeze aún abierto como caja activa para que siempre sea visible
  if (wasSq && sqBars >= minSqueezeBars && sqHigh != null && sqLow != null && sqHigh > sqLow) {
    const lastAtr = atrs[n - 1] ?? 0
    const overlaps = preventOverlap && pendingBoxes.some(b => boxesOverlap(sqHigh, sqLow, b.top, b.bottom))
    if (!overlaps) {
      const lastC = candles[n - 1]
      const halfAtr = lastAtr * 0.5
      const box = {
        startIdx: sqStartIdx, endIdx: n - 1,
        startTime: candles[sqStartIdx].time, endTime: lastC.time,
        top: sqHigh, bottom: sqLow, atr: lastAtr, sqBarCount: sqBars,
        resistanceZoneBottom: sqHigh - halfAtr,
        supportZoneTop:       sqLow + halfAtr,
      }
      pendingBoxes.push(box)
      allBoxes.push(box)
    }
  }

  const lastSig     = signals.length > 0 ? signals[signals.length - 1] : null
  const activeTrade = tradeActive ? { dir, entry, sl, tp1, tp2, tp3 } : null

  return {
    pendingBoxes,
    allBoxes,
    signals,
    sqMarkers,
    activeTrade,
    lastSig,
    dashboard: {
      sqActive:   lastSqActive,
      sqBars:     lastSqBars,
      lastResult: lastSig?.result ?? null,
      lastPnl:    lastSig?.pnl ?? null,
      lastDir:    lastSig?.dir ?? 0,
      strength:   lastSig?.strength ?? 0,
    },
    stats: { win: winCount, loss: lossCount, early: earlyCount },
  }
}

function earlyClose(signals, price, time) {
  const prev = signals[signals.length - 1]
  if (!prev || prev.result !== 'active') return
  prev.pnl = prev.dir === 1 ? price - prev.entry : prev.entry - price
  prev.result = 'early_close'
  prev.closeTime = time
}

// ── render (lightweight-charts primitives) ─────────────────────────────────────

function render(chart, candleSeries, result, config, api) {
  if (!result) return

  const { signals, sqMarkers, activeTrade, allBoxes = [] } = result
  const {
    showBoxes     = true,
    showCenter    = true,
    showZones     = true,
    showSignals   = true,
    showCloseLbls = true,
    colors        = {},
  } = config

  const bull    = colors.bull    ?? '#00E676'
  const bear    = colors.bear    ?? '#FF5252'
  const neutral = colors.neutral ?? '#FFEB3B'

  const MAX_VISIBLE_BOXES   = 40
  const MAX_VISIBLE_SIGNALS = 40
  const visibleBoxes   = allBoxes.slice(-MAX_VISIBLE_BOXES)
  const visibleSignals = signals.slice(-MAX_VISIBLE_SIGNALS)

  if (showBoxes || showCenter || showZones) {
    const common = {
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      lineWidth: 1,
    }

    for (const box of visibleBoxes) {
      if (showBoxes) {
        const topSeries = api.addSeries(chart => chart.addLineSeries({ ...common, color: neutral, lineStyle: 2 }))
        if (topSeries) topSeries.setData([{ time: box.startTime, value: box.top }, { time: box.endTime, value: box.top }])

        const bottomSeries = api.addSeries(chart => chart.addLineSeries({ ...common, color: neutral, lineStyle: 2 }))
        if (bottomSeries) bottomSeries.setData([{ time: box.startTime, value: box.bottom }, { time: box.endTime, value: box.bottom }])
      }

      if (showZones) {
        const resSeries = api.addSeries(chart => chart.addLineSeries({ ...common, color: bear, lineStyle: 1, lineWidth: 1 }))
        if (resSeries) resSeries.setData([{ time: box.startTime, value: box.resistanceZoneBottom }, { time: box.endTime, value: box.resistanceZoneBottom }])

        const supSeries = api.addSeries(chart => chart.addLineSeries({ ...common, color: bull, lineStyle: 1, lineWidth: 1 }))
        if (supSeries) supSeries.setData([{ time: box.startTime, value: box.supportZoneTop }, { time: box.endTime, value: box.supportZoneTop }])
      }

      if (showCenter) {
        const center = (box.top + box.bottom) / 2
        const centerSeries = api.addSeries(chart => chart.addLineSeries({ ...common, color: neutral, lineStyle: 3, lineWidth: 1 }))
        if (centerSeries) centerSeries.setData([{ time: box.startTime, value: center }, { time: box.endTime, value: center }])
      }
    }
  }

  for (const m of sqMarkers) {
    // Marcadores de squeeze desactivados por defecto para reducir ruido visual.
    void m
  }

  for (const sig of visibleSignals) {
    if (showSignals) {
      api.addLabel({
        time: sig.time,
        text: sig.dir === 1 ? 'Long' : 'Short',
        color: sig.dir === 1 ? bull : bear,
        isBull: sig.dir === 1,
        fallbackPrice: sig.entry,
      })
    }

    if (showCloseLbls && sig.closeTime) {
      if (sig.result === 'win') {
        api.addMarker({ time: sig.closeTime, position: sig.dir === 1 ? 'aboveBar' : 'belowBar', color: bull, shape: 'circle', text: '✓', size: 1 })
      } else if (sig.result === 'loss') {
        api.addMarker({ time: sig.closeTime, position: sig.dir === 1 ? 'belowBar' : 'aboveBar', color: bear, shape: 'circle', text: '✗', size: 1 })
      } else if (sig.result === 'early_close') {
        api.addMarker({ time: sig.closeTime, position: sig.dir === 1 ? 'aboveBar' : 'belowBar', color: neutral, shape: 'square', text: '◼', size: 1 })
      }
    }
  }

  if (activeTrade) {
    const { dir: d, entry: en, sl: slv, tp1: t1, tp2: t2, tp3: t3 } = activeTrade
    const tpColor = d === 1 ? bull : bear
    api.addPriceLine({ price: en,  color: d === 1 ? bull : bear, lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: 'Entry' })
    api.addPriceLine({ price: slv, color: bear,                  lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'SL'    })
    api.addPriceLine({ price: t1,  color: tpColor + '99',        lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'TP1'   })
    api.addPriceLine({ price: t2,  color: tpColor + 'bb',        lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'TP2'   })
    api.addPriceLine({ price: t3,  color: tpColor,               lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: 'TP3'   })
  }
}

// ── Default config ──────────────────────────────────────────────────────────────

const defaultConfig = {
  _version:         '1.0',
  squeezeLength:    20,
  bbMult:           2.0,
  squeezeThresh:    0.6,
  atrCompressRatio: 0.75,
  minSqueezeBars:   5,
  impulseMult:      0.8,
  preventOverlap:   true,
  slBuffer:         0.5,
  tp1RR:            1.0,
  tp2RR:            2.0,
  tp3RR:            3.0,
  useVolFilter:     false,
  volMult:          1.5,
  useHtfFilter:     false,
  htfTimeframe:     '1d',
  htfMaLen:         50,
  showBoxes:        true,
  showCenter:       true,
  showZones:        true,
  showSignals:      true,
  showCloseLbls:    true,
  colors: {
    bull:    '#00E676',
    bear:    '#FF5252',
    neutral: '#FFEB3B',
  },
}

export default {
  id:      'smart_breakout',
  name:    'Smart Breakout Targets',
  version: '1.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
}
